import {
  alpha,
  Box,
  Grid,
  Accordion,
  Typography,
  AccordionDetails,
  List,
  ListItem,
  IconButton,
  Stack,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  ListItemButton,
} from '@mui/material';
import { Delete, DragIndicator, ExpandMore, Settings } from '@mui/icons-material';
import React, { MouseEvent, useCallback, useMemo, useState } from 'react';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';
import { FieldInputTypeConfig, FieldInputTypes, Preset } from '@ekkogmbh/apisdk';
import { FieldInputTypePopover } from './FieldInputTypePopover';
import { FormAccordionSummary } from 'src/Common/Styles/FormStyles';

const SortableItem = SortableElement(
  ({
    value,
    readOnly,
    sortIndex,
    onDelete,
    onClick,
    onClickConfig,
    checked,
  }: {
    value: string;
    readOnly: boolean;
    sortIndex: number;
    onDelete: (event: MouseEvent, value: string) => void;
    onClickConfig: (anchor: HTMLButtonElement, value: string) => void;
    onClick?: (value: string) => void;
    checked: boolean;
  }) => (
    <ListItem
      sx={(theme) => ({
        borderTopStyle: 'inset',
        borderTopColor: alpha(theme.palette.text.primary, 0.23),
        borderTopWidth: 1,
        backgroundColor: 'background.paper',
        userSelect: 'none',
      })}
    >
      {!readOnly && (
        <ListItemIcon>
          <DragIndicator color={'primary'} />
        </ListItemIcon>
      )}
      <ListItemButton
        onClick={
          onClick
            ? () => {
                onClick(value);
              }
            : undefined
        }
      >
        <ListItemText>
          <Typography sx={{ color: checked ? 'text.disabled' : 'text.primary' }}>
            <span>{sortIndex + 1}.&nbsp;</span>
            <span>{value}</span>
          </Typography>
        </ListItemText>
      </ListItemButton>
      {!readOnly && (
        <ListItemSecondaryAction>
          <Stack direction={'row'} justifyContent={'flex-end'}>
            <IconButton onClick={(e) => onClickConfig(e.currentTarget, value)}>
              <Settings />
            </IconButton>
            <IconButton onClick={(e: MouseEvent) => onDelete(e, value)} size="large">
              <Delete fontSize={'small'} />
            </IconButton>
          </Stack>
        </ListItemSecondaryAction>
      )}
    </ListItem>
  ),
);

const SortableList = SortableContainer(
  ({
    items,
    readOnly,
    checkedItems,
    onDelete,
    onClickItem,
    onClickConfig,
  }: {
    items: string[];
    types: FieldInputTypes;
    readOnly: boolean;
    checkedItems: string[];
    onDelete: (event: MouseEvent, value: string) => void;
    onClickItem?: (value: string) => void;
    onClickConfig: (anchor: HTMLButtonElement, value: string) => void;
  }) => (
    <List style={{ padding: 4, width: '100%' }}>
      {items.map((value: string, index: number) => (
        <SortableItem
          key={`item-${index}`}
          index={index}
          readOnly={readOnly}
          sortIndex={index}
          value={value}
          onDelete={onDelete}
          onClick={onClickItem}
          checked={checkedItems.includes(value)}
          onClickConfig={onClickConfig}
        />
      ))}
    </List>
  ),
);

interface Props {
  preset: Pick<Preset, 'keys' | 'types'>;
  title?: string;
  selectedKeys?: string[];
  onSelect?: (selected: string[]) => void;
  onChangeKeys?: (keys: string[]) => void;
  onChangeTypes?: (types: FieldInputTypes) => void;
}
export const PresetPanelSorting = ({
  preset,
  selectedKeys,
  title,
  onChangeKeys,
  onChangeTypes,
  onSelect,
}: Props): React.JSX.Element => {
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLButtonElement>();
  const [fieldTypeKey, setFieldTypeKey] = useState<string>();
  const isReadOnly = onChangeKeys === undefined;

  const selected = useMemo(() => selectedKeys ?? [], [selectedKeys]);
  const keys = useMemo(() => preset.keys ?? [], [preset.keys]);
  const types = useMemo(() => preset.types, [preset.types]);

  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
      const newKeys = arrayMoveImmutable(keys, oldIndex, newIndex);
      if (onChangeKeys) {
        onChangeKeys(newKeys);
      }
    },
    [keys, onChangeKeys],
  );

  const onDeleteKey = useCallback(
    (event: React.MouseEvent, value: string): void => {
      event.preventDefault();
      const valueIndex = keys.indexOf(value);
      if (valueIndex >= 0) {
        keys.splice(valueIndex, 1);
        if (onChangeKeys) {
          onChangeKeys([...keys]);
        }

        delete types[value];
        if (onChangeTypes) {
          onChangeTypes(types);
        }
      }
    },
    [keys, types, onChangeKeys, onChangeTypes],
  );

  const onToggleKey = useCallback(
    (value: string) => {
      const keyIndex = selected.indexOf(value) ?? -1;

      if (keyIndex >= 0) {
        selected.splice(keyIndex, 1);
      } else {
        selected.push(value);
      }
      if (onSelect) {
        onSelect([...selected]);
      }
    },
    [selected, onSelect],
  );

  const togglePopover = useCallback((anchor?: HTMLButtonElement, value?: string): void => {
    setPopoverAnchor(anchor);
    setFieldTypeKey(value);
  }, []);

  return (
    <Grid container spacing={2} alignItems={'stretch'} style={{ position: 'relative' }}>
      {fieldTypeKey && popoverAnchor && (
        <FieldInputTypePopover
          initialConfig={types[fieldTypeKey]}
          onChangeCallback={(conf: FieldInputTypeConfig) => {
            if (onChangeTypes !== undefined) {
              onChangeTypes({ ...types, [fieldTypeKey]: conf });
            }
          }}
          popoverProps={{
            open: true,
            anchorEl: popoverAnchor,
            anchorOrigin: {
              horizontal: 'left',
              vertical: 'bottom',
            },
            transformOrigin: {
              horizontal: 'right',
              vertical: 'top',
            },
            onClose: () => togglePopover(undefined),
            style: {
              maxWidth: '500px',
            },
          }}
        />
      )}
      <Grid item xs={12}>
        {
          <Box
            sx={(theme) => ({
              margin: 1,
              padding: 0,
              borderStyle: 'solid',
              borderRadius: 0.5,
              borderWidth: 1,
              borderColor: alpha(theme.palette.text.primary, 0.23),
            })}
          >
            <Accordion
              style={{
                padding: 0,
                minHeight: 54,
              }}
              elevation={0}
              defaultExpanded={true}
            >
              <FormAccordionSummary expandIcon={<ExpandMore />}>
                <Typography color={'primary'}>{title ?? 'Key-Order'}</Typography>
                {selectedKeys && (
                  <Typography color={'primary'} style={{ paddingLeft: '20px' }}>
                    click on items to enable or disable them
                  </Typography>
                )}
              </FormAccordionSummary>
              <AccordionDetails style={{ padding: 0 }}>
                <SortableList
                  lockAxis={'y'}
                  distance={5}
                  items={keys}
                  types={types}
                  readOnly={isReadOnly}
                  shouldCancelStart={() => isReadOnly}
                  onSortEnd={onSortEnd}
                  onDelete={onDeleteKey}
                  onClickItem={onSelect ? onToggleKey : undefined}
                  onClickConfig={togglePopover}
                  checkedItems={selected}
                />
              </AccordionDetails>
            </Accordion>
          </Box>
        }
      </Grid>
    </Grid>
  );
};
